import { useState, FormEvent } from 'react';

interface StageInfo {
  stage_number: number;
  stage_name: string;
  completed: boolean;
}

interface AppointmentInfo {
  id: string;
  status: string;
  confirmed_date: string | null;
  confirmed_time: string | null;
  current_date: string;
  current_time: string;
  created_at: string;
}

interface CollectionInfo {
  collection_address: string | null;
  preferred_collection_date: string | null;
  preferred_collection_time: string | null;
  assigned_officer: string | null;
  collection_status: string;
}

interface TrackingData {
  reference_number: string;
  client_name: string;
  country_of_travel: string;
  current_stage: number;
  final_decision: string;
  submitted_at: string;
  application_type: string | null;
  stages: StageInfo[];
  appointment: AppointmentInfo | null;
  collection: CollectionInfo | null;
}

const stageIcons: Record<number, string> = {
  1: 'ri-file-list-3-line',
  2: 'ri-truck-line',
  3: 'ri-file-search-line',
  4: 'ri-check-double-line',
  5: 'ri-calendar-check-line',
  6: 'ri-government-line',
  7: 'ri-time-line',
  8: 'ri-question-answer-line',
  9: 'ri-mail-check-line',
  10: 'ri-scales-line',
  11: 'ri-passport-line',
};

const COLLECTION_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function TrackApplication() {
  const [refNumber, setRefNumber] = useState('');
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setTrackingData(null);
    setSearched(true);
    setLoading(true);

    const trimmed = refNumber.trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter a PVS reference number.');
      setLoading(false);
      return;
    }

    if (!trimmed.startsWith('PVS-')) {
      setError('Reference numbers follow the format: PVS-YYYY-XXXX');
      setLoading(false);
      return;
    }

    try {
      const url = `${SUPABASE_URL}/functions/v1/track-application`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ reference_number: trimmed }),
      });

      const responseText = await response.text();
      let parsed: { error?: string } & TrackingData;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        console.error('Track application: invalid JSON response', responseText);
        setError('We could not retrieve your application at this time. Please try again or contact Premium Visa Solutions.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        console.error('Track application: non-2xx response', response.status, parsed);
        setError(parsed?.error || 'We could not retrieve your application at this time. Please try again or contact Premium Visa Solutions.');
        setLoading(false);
        return;
      }

      if (parsed?.error) {
        setError(parsed.error);
        setLoading(false);
        return;
      }

      setTrackingData(parsed as TrackingData);
    } catch (err: unknown) {
      console.error('Track application: fetch error', err);
      setError('We could not retrieve your application at this time. Please try again or contact Premium Visa Solutions.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isOfficeAppointment = trackingData?.application_type === 'office_appointment';
  const isHomeCollection = trackingData?.application_type === 'home_collection';

  // ──── Office Appointment Section ────
  const renderOfficeAppointmentSection = (appt: AppointmentInfo) => {
    const isConfirmed = appt.status === 'confirmed';
    const isCancelled = appt.status === 'cancelled';
    const isCompleted = appt.status === 'completed';

    return (
      <div className="mb-6">
        {/* Confirmed Banner */}
        {isConfirmed && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-calendar-check-line text-green-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">Your Office Appointment is Confirmed</p>
                <p className="text-sm text-green-700">
                  <strong>{formatDateDisplay(appt.current_date)}</strong> at <strong>{appt.current_time}</strong>
                </p>
                <p className="text-xs text-green-600 mt-0.5">Please visit our office with your original documents at the confirmed time.</p>
              </div>
            </div>
          </div>
        )}

        {/* Cancelled Banner */}
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-red-800">Office Appointment Cancelled</p>
                <p className="text-sm text-red-700">This appointment has been cancelled. Please contact us to arrange a new appointment.</p>
              </div>
            </div>
          </div>
        )}

        {/* Completed Banner */}
        {isCompleted && (
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-200 rounded-full flex-shrink-0">
                <i className="ri-check-double-line text-foreground-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">Office Visit Completed</p>
                <p className="text-sm text-foreground-600">Your office visit has been completed successfully.</p>
              </div>
            </div>
          </div>
        )}

        {/* Appointment Summary Card */}
        <div className="bg-background-50 border border-secondary-200 rounded-lg p-5">
          <h4 className="font-heading font-semibold text-base text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-building-line text-primary-600"></i> Office Appointment Details
          </h4>
          <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3 mb-3">
            <p className="text-xs text-foreground-500 mb-1">Office Address</p>
            <p className="text-sm font-medium text-foreground-900">Premium Visa Solutions, Lusaka, Zambia</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-foreground-500">Appointment Status</p>
              <p className={`font-semibold ${isConfirmed ? 'text-green-700' : isCancelled ? 'text-red-700' : 'text-foreground-900'}`}>
                {isConfirmed ? 'CONFIRMED' : isCancelled ? 'Cancelled' : isCompleted ? 'Completed' : appt.status}
              </p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Appointment Date</p>
              <p className="font-semibold text-foreground-900">{formatDateDisplay(appt.current_date)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Appointment Time</p>
              <p className="font-semibold text-foreground-900">{appt.current_time}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ──── Home Collection Section ────
  const renderHomeCollectionSection = (collection: CollectionInfo) => {
    const isPending = collection.collection_status === 'pending';
    const isConfirmed = collection.collection_status === 'confirmed';
    const isCompleted = collection.collection_status === 'completed';

    return (
      <div className="mb-6">
        {isPending && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                <i className="ri-time-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">Document Collection Pending</p>
                <p className="text-sm text-amber-700">
                  Requested: <strong>{formatDateDisplay(collection.preferred_collection_date || '')}</strong> at <strong>{collection.preferred_collection_time || '—'}</strong>
                </p>
                <p className="text-xs text-amber-600 mt-0.5">Our team is reviewing your collection request. You will be notified when confirmed.</p>
              </div>
            </div>
          </div>
        )}

        {isConfirmed && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-calendar-check-line text-green-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">Document Collection Confirmed</p>
                <p className="text-sm text-green-700">
                  <strong>{formatDateDisplay(collection.preferred_collection_date || '')}</strong> at <strong>{collection.preferred_collection_time || '—'}</strong>
                </p>
                {collection.assigned_officer && (
                  <p className="text-xs text-green-600 mt-0.5">
                    <i className="ri-user-line mr-1"></i> Collection Officer: <strong>{collection.assigned_officer}</strong>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-200 rounded-full flex-shrink-0">
                <i className="ri-check-double-line text-foreground-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">Documents Collected</p>
                <p className="text-sm text-foreground-600">Your documents have been successfully collected.</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-background-50 border border-secondary-200 rounded-lg p-5">
          <h4 className="font-heading font-semibold text-base text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-home-4-line text-accent-600"></i> Document Collection Details
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="sm:col-span-2">
              <p className="text-xs text-foreground-500">Collection Address</p>
              <p className="font-medium text-foreground-900">{collection.collection_address || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Collection Status</p>
              <p className="font-semibold text-foreground-900">{COLLECTION_STATUS_LABELS[collection.collection_status] || collection.collection_status}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Preferred Collection Date</p>
              <p className="font-medium text-foreground-800">{formatDateDisplay(collection.preferred_collection_date || '')}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Preferred Collection Time</p>
              <p className="font-medium text-foreground-800">{collection.preferred_collection_time || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Assigned Officer</p>
              <p className="font-medium text-foreground-800">{collection.assigned_officer || 'Not yet assigned'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20navy%20background%20with%20minimalist%20timeline%20dots%20and%20connecting%20lines%20in%20orange%2C%20modern%20clean%20tracking%20concept%2C%20professional%20corporate%20design%2C%20subtle%20progress%20indicators&width=1920&height=600&seq=track-application-visa&orientation=landscape"
          alt="Track your visa application"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Track Your Application - Premium Visa Solutions"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Track Your Application</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[550px] mx-auto">
            Monitor your visa application progress in real-time using your PVS reference number
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[800px] mx-auto px-4 md:px-8">
          <div className="bg-secondary-100 border border-secondary-200 rounded-lg p-6 md:p-8 mb-10">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Enter Your PVS Reference Number</h3>
            <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3">
              <input type="text" value={refNumber} onChange={(e) => setRefNumber(e.target.value)} placeholder="e.g. PVS-2026-0001" className="flex-1 px-4 py-3 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
              <button type="submit" disabled={loading} className="px-10 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 disabled:opacity-60 transition-colors text-sm whitespace-nowrap cursor-pointer">
                {loading ? 'Searching...' : 'Track Application'}
              </button>
            </form>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            <p className="text-xs text-foreground-500 mt-3">Your reference number was provided when you submitted your application. It follows the format: PVS-YYYY-XXXX.</p>
          </div>

          {trackingData && (
            <div>
              {/* Application Type Badge */}
              <div className="flex items-center gap-2 mb-5">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                  isOfficeAppointment ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                }`}>
                  <i className={`text-sm ${isOfficeAppointment ? 'ri-building-line' : 'ri-home-4-line'}`}></i>
                  {isOfficeAppointment ? 'Office Appointment' : 'Home Collection'}
                </span>
              </div>

              {/* Office Appointment Section */}
              {isOfficeAppointment && trackingData.appointment && renderOfficeAppointmentSection(trackingData.appointment)}

              {/* Home Collection Section */}
              {isHomeCollection && trackingData.collection && renderHomeCollectionSection(trackingData.collection)}

              {/* Application Summary Card */}
              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs text-foreground-500">Reference Number</p>
                    <p className="font-heading text-lg font-bold text-primary-700">{trackingData.reference_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">Applicant</p>
                    <p className="font-semibold text-foreground-900">{trackingData.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">Destination</p>
                    <p className="font-semibold text-foreground-900">{trackingData.country_of_travel}</p>
                  </div>
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      trackingData.final_decision === 'approved' ? 'bg-green-100 text-green-700' :
                      trackingData.final_decision === 'declined' ? 'bg-red-100 text-red-700' :
                      'bg-accent-100 text-accent-700'
                    }`}>
                      {trackingData.final_decision === 'approved' ? 'Approved' : trackingData.final_decision === 'declined' ? 'Declined' : 'In Progress'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress Timeline */}
              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-8">
                <h4 className="font-heading font-semibold text-lg text-foreground-900 mb-6">Application Progress</h4>
                <div className="space-y-0">
                  {trackingData.stages.map((stage, idx) => {
                    const isCompleted = stage.completed;
                    const isCurrent = stage.stage_number === trackingData.current_stage;
                    const isDeclinedStage = trackingData.final_decision === 'declined' && stage.stage_number === 10;

                    return (
                      <div key={idx} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${
                            isDeclinedStage ? 'bg-red-100 text-red-600' :
                            isCompleted ? 'bg-primary-500 text-background-50' :
                            isCurrent ? 'bg-accent-500 text-background-50' :
                            'bg-secondary-200 text-foreground-400'
                          }`}>
                            {isDeclinedStage ? <i className="ri-close-line text-sm"></i> :
                             isCompleted ? <i className="ri-check-line text-sm"></i> :
                             <i className={`${stageIcons[stage.stage_number] || 'ri-circle-line'} text-sm`}></i>}
                          </div>
                          {idx < trackingData.stages.length - 1 && (
                            <div className={`w-0.5 flex-1 min-h-[28px] ${stage.completed ? 'bg-primary-300' : 'bg-secondary-200'}`}></div>
                          )}
                        </div>
                        <div className={`pb-6 ${idx === trackingData.stages.length - 1 ? 'pb-0' : ''}`}>
                          <p className={`text-sm font-medium ${
                            isDeclinedStage ? 'text-red-700' :
                            isCompleted ? 'text-primary-700' :
                            isCurrent ? 'text-accent-700' :
                            'text-foreground-500'
                          }`}>{stage.stage_name}</p>
                          {isCurrent && <p className="text-xs text-accent-600 mt-0.5">Current stage</p>}
                          {isDeclinedStage && <p className="text-xs text-red-600 mt-0.5">Application was not successful</p>}
                          {stage.stage_number === 11 && isCompleted && trackingData.final_decision === 'approved' && (
                            <p className="text-xs text-green-600 mt-0.5">Your passport is ready for collection</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {searched && !trackingData && !error && !loading && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-secondary-200 rounded-full mb-4">
                <i className="ri-search-line text-2xl text-foreground-500"></i>
              </div>
              <p className="text-foreground-600 text-sm">No results found. Please check your reference number.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}